import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { OverageDecision } from '@gigdriver/domain';
import { currentDecision } from '@gigdriver/domain';
import { recordOverageDecision, watchOverageDecisions, type RecordOverageDecisionInput } from '@gigdriver/client';

// One gig's overage decisions (append-only audit trail) and the action that
// records a new one. Coordinator+ may read; only manager+ can record (rules).
export const useOverageStore = defineStore('overage', () => {
  const decisions = ref<OverageDecision[]>([]);
  const isSaving = ref(false);
  const error = ref<string | null>(null);

  const current = computed(() => currentDecision(decisions.value));

  let stop: (() => void) | null = null;

  function watchGig(orgId: string, gigId: string) {
    stop?.();
    decisions.value = [];
    stop = watchOverageDecisions(orgId, gigId, d => (decisions.value = d), err => {
      error.value = err instanceof Error ? err.message : 'Failed to load overage decisions';
    });
  }

  function stopWatching() {
    stop?.();
    stop = null;
  }

  async function record(orgId: string, gigId: string, input: RecordOverageDecisionInput) {
    isSaving.value = true;
    error.value = null;
    try {
      return await recordOverageDecision(orgId, gigId, input);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to record decision';
      throw err;
    } finally {
      isSaving.value = false;
    }
  }

  return { decisions, current, isSaving, error, watchGig, stopWatching, record };
});
